import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Filter, X, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { getPaymentDate } from "@/lib/paymentDate";
import type { DateRange } from "react-day-picker";

interface FiltersSectionProps {
  selectedMonth: string;
  setSelectedMonth: (value: string) => void;
  selectedYear: string;
  setSelectedYear: (value: string) => void;
  selectedDay: string;
  setSelectedDay: (value: string) => void;
  dateRange?: DateRange;
  setDateRange: (range: DateRange | undefined) => void;
  payments: any[] | undefined;
  onClearFilters: () => void;
}

export const FiltersSection = ({
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  selectedDay,
  setSelectedDay,
  dateRange,
  setDateRange,
  payments,
  onClearFilters
}: FiltersSectionProps) => {
  const getEffectiveDate = (payment: any) => getPaymentDate(payment);

  const getUniqueMonths = () => {
    if (!payments) return [];
    const months = new Set(
      payments.map((payment) =>
        format(getEffectiveDate(payment), "MMMM", { locale: es })
      )
    );
    return Array.from(months);
  };

  const getUniqueYears = () => {
    if (!payments) return [];
    const years = new Set(
      payments.map((payment) =>
        getEffectiveDate(payment).getFullYear().toString()
      )
    );
    return Array.from(years);
  };

  const getUniqueDays = () => {
    if (!payments) return [];
    const days = new Set(
      payments.map((payment) =>
        getEffectiveDate(payment).getDate().toString()
      )
    );
    return Array.from(days).sort((a, b) => Number(a) - Number(b));
  };

  const hasActiveFilters =
    selectedMonth !== "all_months" ||
    selectedYear !== "all_years" ||
    selectedDay !== "all_days" ||
    !!dateRange?.from;

  const formatRangeLabel = () => {
    if (!dateRange?.from) return "Calendario";
    const fromStr = format(dateRange.from, "d MMM yyyy", { locale: es });
    if (!dateRange.to || dateRange.to.getTime() === dateRange.from.getTime()) {
      return fromStr;
    }
    const toStr = format(dateRange.to, "d MMM yyyy", { locale: es });
    return `${fromStr} – ${toStr}`;
  };

  return (
    <div className="glass-card rounded-xl border border-primary/10 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-2 text-primary-foreground/70">
          <Filter className="h-4 w-4" />
          <span className="text-sm font-medium text-primary-foreground">Filtros</span>
        </div>

        <div className="flex flex-wrap gap-3 flex-1">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-full sm:w-[160px] bg-background/50 border-primary/10 text-primary">
              <SelectValue placeholder="Mes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_months">Todos los meses</SelectItem>
              {getUniqueMonths().map((month) => (
                <SelectItem key={month} value={month}>
                  {month.charAt(0).toUpperCase() + month.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-full sm:w-[140px] bg-background/50 border-primary/10 text-primary">
              <SelectValue placeholder="Año" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_years">Todos los años</SelectItem>
              {getUniqueYears().map((year) => (
                <SelectItem key={year} value={year}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedDay} onValueChange={setSelectedDay}>
            <SelectTrigger className="w-full sm:w-[140px] bg-background/50 border-primary/10 text-primary">
              <SelectValue placeholder="Día" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_days">Todos los días</SelectItem>
              {getUniqueDays().map((day) => (
                <SelectItem key={day} value={day}>
                  Día {day}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full sm:w-auto justify-start text-left font-normal bg-background/50 border-primary/10 text-primary gap-2",
                  !dateRange?.from && "text-primary/70"
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                {formatRangeLabel()}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-black/70 backdrop-blur-xl border-primary/20" align="start">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
              {dateRange?.from && (
                <div className="flex justify-end p-2 border-t border-primary/10">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDateRange(undefined)}
                    className="text-primary/80 hover:text-primary gap-1.5"
                  >
                    <X className="h-4 w-4" />
                    Limpiar rango
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-primary/60 hover:text-primary hover:bg-primary/10 gap-1.5"
          >
            <X className="h-4 w-4" />
            Limpiar
          </Button>
        )}
      </div>
    </div>
  );
};
